import React, { useEffect } from "react";
import "./styles.css";

import { Input } from "../../../../components/common/Input";
import { Button } from "../../../../components/common/Button";
import { ReactComponent as ShareIcon } from "../../../../assets/icons/share.svg";
import { RootState } from "../../../../store";
import { HousingsActionCreator } from "../../../../store/housingsActions";
import { connect } from "react-redux";
import {
  selectIsTestPeriod,
  selectSharingDataCode,
  selectSharingDataId,
  selectSharingError,
  selectSharingPending,
  selectUserData,
  selectUserInfo,
} from "../../../../store/selectors";
import { formatHousingObject, formatSharingData } from "./utils";
import { Error } from "../../../../components/common/Error";
import { TEST_SHARE_DATA } from "./constants";
import { InfoBar } from "../../../../components/common/InfoBar";
import { useBranding } from "../../../../integrations/branding";
import {
  ANALYTICS_EVENTS,
  buildAnalyticsUserContext,
  createShareAnalyticsSnapshot,
  getCalculationAnalyticsProperties,
  trackAnalyticsEvent,
} from "../../../../analytics";

// модалка прикрепления расчёта к сделке
import { AttachCalcToDealModal } from "./AttachCalcToDeal"; // поправь путь, если файл в другом месте

const normalizeSharedPropertyType = (value) => {
  if (value === "commerce" || value === "commercial") {
    return "commercial";
  }
  if (value === "parking") {
    return "parking";
  }
  return "apartment";
};

export const _ShareUSP = ({
                            pending,
                            error,
                            sharingDataId,
                            sharingDataCode,
                            shareData,
                            fetchSharingDataId,
                            resetSharingDataId,
                            housing,
                            propertyType,
                            testPeriod,
                          user,
                          userData,
                          }) => {
  const { brandId, isShowcase, config } = useBranding();
  const [isAttachOpen, setIsAttachOpen] = React.useState(false);
  const [lastWhatsappUrl, setLastWhatsappUrl] = React.useState("");
  const whatsappWindowRef = React.useRef<Window | null>(null);
  const trackedSharingIdRef = React.useRef<string | number | null>(null);
  const shareRequestStartedRef = React.useRef(false);

  const origin = window?.location.origin;
  const showTestPeriodWarning = Boolean(testPeriod);
  const sharedUrlParams = new URLSearchParams();
  if (isShowcase && brandId) {
    sharedUrlParams.set("brand", brandId);
  }
  const sharedQuery = sharedUrlParams.toString();
  const shareUrl = `${origin}/shared/housing/${sharingDataCode}${sharedQuery ? `?${sharedQuery}` : ""}`;
  const whatsappNumber = String(config.showcase.whatsappNumber || "").replace(/\D/g, "");
  const hasShowcaseWhatsapp = isShowcase && Boolean(whatsappNumber);
  const whatsappButtonLabel =
    config.showcase.whatsappButtonLabel || "Отправить расчёт в WhatsApp";

  const finalHousingData = formatHousingObject(housing);
  const formattedShareData = formatSharingData(shareData);
  const normalizedPropertyType = normalizeSharedPropertyType(propertyType);
  const sharingUserId = user?.id ?? (isShowcase ? 1 : null);

  const finalShareData = {
    ...(showTestPeriodWarning
      ? TEST_SHARE_DATA
      : {
        ...formattedShareData,
        housingData: finalHousingData,
      }),
    propertyType: normalizedPropertyType,
  };
  const calculationAnalyticsProperties = React.useMemo(
    () =>
      getCalculationAnalyticsProperties(
        {
          ...shareData,
          propertyType: normalizedPropertyType,
        },
        housing,
      ),
    [housing, normalizedPropertyType, shareData],
  );

  useEffect(() => {
    if (sharingDataId) {
      resetSharingDataId();
    }
    setLastWhatsappUrl("");
    if (whatsappWindowRef.current && !whatsappWindowRef.current.closed) {
      whatsappWindowRef.current.close();
    }
    whatsappWindowRef.current = null;
  }, [shareData]);

  useEffect(() => {
    if (
      !sharingDataId ||
      trackedSharingIdRef.current === sharingDataId
    ) {
      return;
    }

    trackedSharingIdRef.current = sharingDataId;
    shareRequestStartedRef.current = false;
    trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_SHARE_CREATED, {
      ...calculationAnalyticsProperties,
      share_id: sharingDataId,
      share_code: sharingDataCode,
      share_channel: hasShowcaseWhatsapp ? "whatsapp" : "link",
      branded_showcase: isShowcase,
      brand_id: brandId || undefined,
    });
  }, [
    brandId,
    calculationAnalyticsProperties,
    hasShowcaseWhatsapp,
    isShowcase,
    sharingDataCode,
    sharingDataId,
  ]);

  useEffect(() => {
    if (hasShowcaseWhatsapp) {
      return;
    }

    if (navigator.clipboard && sharingDataId) {
      navigator.clipboard.writeText(shareUrl).then(
          function () {
            trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_LINK_COPIED, {
              ...calculationAnalyticsProperties,
              share_id: sharingDataId,
              share_code: sharingDataCode,
              copy_source: "automatic",
            });
          },
          function (err) {
          console.error("Произошла ошибка при копировании текста: ", err);
          },
      );
    }
  }, [
    calculationAnalyticsProperties,
    hasShowcaseWhatsapp,
    shareUrl,
    sharingDataCode,
    sharingDataId,
  ]);

  useEffect(() => {
    if (sharingDataId && testPeriod) {
      // если нужно вернуть уведомление — можно раскомментировать и адаптировать
      // setNotification({
      //   isOpen: true,
      //   value: {
      //     title: "Тестовый период",
      //     description:
      //       "Обратите внимание, что используется тестовая версия HusamHelper.\n" +
      //       "По ссылке будут отображены тестовые данные. Чтобы воспользоваться полной версией, оформите подписку",
      //     link: {
      //       title: "оформить подписку",
      //       href: "/payment",
      //     },
      //     closeIcon: true,
      //   },
      // });
    }
  }, [sharingDataId]);

  useEffect(() => {
    if (!hasShowcaseWhatsapp || !sharingDataId) {
      return;
    }

    const message = [
      shareData?.housingName ? `Расчёт по объекту ${shareData.housingName}` : "Расчёт по объекту",
      shareUrl,
    ].join("\n");
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    setLastWhatsappUrl(whatsappUrl);
    trackAnalyticsEvent(
      ANALYTICS_EVENTS.CALCULATION_NATIVE_SHARE_SUCCEEDED,
      {
        ...calculationAnalyticsProperties,
        share_id: sharingDataId,
        share_code: sharingDataCode,
        share_channel: "whatsapp",
        branded_showcase: true,
        brand_id: brandId || undefined,
      },
    );

    if (whatsappWindowRef.current && !whatsappWindowRef.current.closed) {
      whatsappWindowRef.current.location.href = whatsappUrl;
    } else {
      window.location.assign(whatsappUrl);
    }

    whatsappWindowRef.current = null;
    resetSharingDataId();
  }, [
    hasShowcaseWhatsapp,
    brandId,
    calculationAnalyticsProperties,
    shareData?.housingName,
    shareUrl,
    sharingDataCode,
    sharingDataId,
    resetSharingDataId,
    whatsappNumber,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }

    if (
      hasShowcaseWhatsapp &&
      whatsappWindowRef.current &&
      !whatsappWindowRef.current.closed
    ) {
      whatsappWindowRef.current.close();
    }
    whatsappWindowRef.current = null;
    if (shareRequestStartedRef.current) {
      shareRequestStartedRef.current = false;
      trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_SHARE_FAILED, {
        ...calculationAnalyticsProperties,
        share_channel: hasShowcaseWhatsapp ? "whatsapp" : "link",
        failure_stage: "create_link",
      });
    }
  }, [calculationAnalyticsProperties, error, hasShowcaseWhatsapp]);

  if (!shareData?.fullPrice) {
    return null;
  }

  const shareDataObject = {
    title: shareData.housingName,
    text: "Только посмотри, что для тебя подобрали!",
    url: shareUrl,
  };

  const handleShare = async () => {
    try {
      if (typeof navigator.share !== "function") {
        throw new Error("Web Share API is unavailable");
      }
      await navigator.share(shareDataObject);
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CALCULATION_NATIVE_SHARE_SUCCEEDED,
        {
          ...calculationAnalyticsProperties,
          share_id: sharingDataId,
          share_code: sharingDataCode,
          share_channel: "native",
        },
      );
    } catch (err) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_NATIVE_SHARE_FAILED, {
        ...calculationAnalyticsProperties,
        share_id: sharingDataId,
        share_code: sharingDataCode,
        share_channel: "native",
        failure_reason:
          err instanceof Error ? err.name || "unknown" : "unknown",
      });
    }
  };

  const handleGetShareLink = () =>
    {
      shareRequestStartedRef.current = true;
      trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_SHARE_REQUESTED, {
        ...calculationAnalyticsProperties,
        share_channel: hasShowcaseWhatsapp ? "whatsapp" : "link",
        branded_showcase: isShowcase,
        brand_id: brandId || undefined,
      });

      if (hasShowcaseWhatsapp) {
        whatsappWindowRef.current = window.open("", "_blank");
      }

      return fetchSharingDataId({
        content: {
          ...finalShareData,
          analyticsContext: {
            ...createShareAnalyticsSnapshot(),
            ...buildAnalyticsUserContext(user, userData),
            ...calculationAnalyticsProperties,
          },
        },
        userId: sharingUserId,
      });
    };

  if (hasShowcaseWhatsapp) {
    return (
      <>
        {error && <Error title="Не удалось подготовить ссылку для WhatsApp" />}
        <div className="share-usp">
          <Button
            pending={pending}
            text={error ? "Попробовать ещё раз" : whatsappButtonLabel}
            onClick={handleGetShareLink}
            fullWidth
            className="share-usp_primary-button"
          />
          {lastWhatsappUrl && !pending && (
            <div className="share-usp_attach">
              <Button
                variant="secondary"
                size="sm"
                text="Открыть чат ещё раз"
                onClick={() => {
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.CALCULATION_NATIVE_SHARE_SUCCEEDED,
                    {
                      ...calculationAnalyticsProperties,
                      share_id: sharingDataId,
                      share_code: sharingDataCode,
                      share_channel: "whatsapp_reopen",
                    },
                  );
                  window.open(lastWhatsappUrl, "_blank");
                }}
                fullWidth
                className="share-usp_attach-button"
              />
            </div>
          )}
        </div>
      </>
    );
  }

  if (sharingDataId) {
    return (
        <>
          <div className="share-usp">
            {showTestPeriodWarning && (
                <InfoBar
                    text="Обратите внимание, что используется тестовая версия HusamHelper. По ссылке будут отображены тестовые данные. Чтобы воспользоваться полной версией, оформите"
                    link={{ href: "/payment", text: " подписку" }}
                />
            )}

            <div className="share-block_container">
              <Input value={shareUrl} />
              <Button
                  variant="secondary"
                  size="sm"
                  icon={<ShareIcon height="18px" width="18px" />}
                  iconOnly
                  onClick={handleShare}
                  className="share-usp_icon-button"
              />
            </div>

            <div className="share-usp_attach">
              <Button
                  variant="secondary"
                  size="sm"
                  text="Прикрепить к сделке"
                  onClick={() => setIsAttachOpen(true)}
                  fullWidth
                  className="share-usp_attach-button"
              />
            </div>
          </div>
          <AttachCalcToDealModal
              isOpen={isAttachOpen}
              onClose={() => setIsAttachOpen(false)}
              sharingId={sharingDataId}
          />
        </>
    );
  } else {
    return (
        <>
          {error && <Error title="Не удалось получить ссылку" />}
          <div className="share-usp">
            <Button
                pending={pending}
                text={error ? "Попробовать ещё раз" : "Получить ссылку"}
                onClick={handleGetShareLink}
                fullWidth
                className="share-usp_primary-button"
            />
          </div>
        </>
    );
  }
};

const mapStateToProps = (state: RootState) => ({
  pending: selectSharingPending(state),
  error: selectSharingError(state),
  sharingDataId: selectSharingDataId(state),
  sharingDataCode: selectSharingDataCode(state),
  testPeriod: selectIsTestPeriod(state),
  user: selectUserInfo(state),
  userData: selectUserData(state),
});

const mapDispatchToProps = {
  fetchSharingDataId: HousingsActionCreator.fetchSharingDataId,
  resetSharingDataId: HousingsActionCreator.sharingDataIdReset,
};

export const ShareUSP = connect(mapStateToProps, mapDispatchToProps)(_ShareUSP);
